import { router } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAuthStore } from '@/store/auth-store';
import { colors, radius, spacing } from '@/theme/tokens';

const CODE_LENGTH = 6;
/** Mirrors the backend resend cooldown (30 s) so the UI never offers a resend the server would reject. */
const RESEND_COOLDOWN_S = 30;

/** "+48500600700" -> "+48 500 ••• 700" — confirm the number without re-printing it whole. */
function maskPhone(phone: string): string {
  const digits = phone.replace(/^\+48/, '');
  if (digits.length !== 9) return phone;
  return `+48 ${digits.slice(0, 3)} ••• ${digits.slice(6)}`;
}

/**
 * Login step 2 — mock: logowanie_kod_sms/screen.png. One hidden input drives six
 * visual boxes (robust for paste + SMS autofill); resend button counts the same
 * 30 s the backend enforces.
 */
export default function AuthCodeScreen() {
  const { t } = useTranslation();
  const pendingPhone = useAuthStore((s) => s.pendingPhone);
  const verifyCode = useAuthStore((s) => s.verifyCode);
  const requestCode = useAuthStore((s) => s.requestCode);

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  if (!pendingPhone) {
    // Deep-linked or reloaded mid-flow: start over instead of a dead screen.
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
        <View style={{ padding: spacing.screen, gap: spacing.md }}>
          <ScreenHeader title={t('auth.loginTitle')} />
          <AppText variant="bodyLg">{t('auth.startOver')}</AppText>
          <PrimaryButton label={t('auth.phoneTitle')} onPress={() => router.replace('/auth/phone')} />
        </View>
      </SafeAreaView>
    );
  }

  const onVerify = async () => {
    setBusy(true);
    setError(null);
    const result = await verifyCode(code);
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? t('auth.genericError'));
      return;
    }
    const match = useAuthStore.getState().matchStatus;
    if (match === 'SINGLE_MASKED' || match === 'MULTIPLE') {
      router.replace('/auth/connect');
    } else {
      router.dismissTo('/menu');
    }
  };

  const onResend = async () => {
    setError(null);
    const result = await requestCode(pendingPhone);
    if (result.ok) {
      setCooldown(RESEND_COOLDOWN_S);
      setCode('');
    } else {
      setError(result.message ?? t('auth.genericError'));
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('auth.loginTitle')} />

        <AppText variant="headlineLg" color={colors.navy}>
          {t('auth.codeTitle')}
        </AppText>
        <AppText variant="bodyLg">
          {t('auth.codeSubtitle')}{' '}
          <AppText variant="labelLg" color={colors.primary}>
            {maskPhone(pendingPhone)}
          </AppText>
          .
        </AppText>

        {/* Hidden input + six boxes */}
        <Pressable onPress={() => inputRef.current?.focus()}>
          <View style={{ flexDirection: 'row', gap: spacing.sm, justifyContent: 'center' }}>
            {Array.from({ length: CODE_LENGTH }, (_, i) => (
              <View
                key={i}
                style={{
                  width: 48,
                  height: 60,
                  borderRadius: radius.card,
                  backgroundColor: colors.primaryTile,
                  borderWidth: i === code.length ? 2 : 0,
                  borderColor: colors.primary,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AppText variant="headlineMd" color={colors.navy}>
                  {code[i] ?? ''}
                </AppText>
              </View>
            ))}
          </View>
          <TextInput
            ref={inputRef}
            value={code}
            onChangeText={(text) => setCode(text.replace(/\D/g, '').slice(0, CODE_LENGTH))}
            keyboardType="number-pad"
            autoFocus
            textContentType="oneTimeCode"
            style={{ position: 'absolute', opacity: 0, height: 1, width: 1 }}
          />
        </Pressable>

        <Pressable
          accessibilityRole="button"
          disabled={cooldown > 0}
          onPress={onResend}
          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, minHeight: 44 }}
        >
          <Ionicons name="time-outline" size={18} color={cooldown > 0 ? colors.outlineVariant : colors.primary} />
          <AppText variant="labelLg" color={cooldown > 0 ? colors.outlineVariant : colors.primary}>
            {cooldown > 0 ? t('auth.resendIn', { seconds: cooldown }) : t('auth.resend')}
          </AppText>
        </Pressable>

        {error && (
          <AppText variant="bodyMd" color={colors.warning} style={{ textAlign: 'center' }}>
            {error}
          </AppText>
        )}

        <PrimaryButton
          label={t('auth.login')}
          disabled={code.length !== CODE_LENGTH || busy}
          onPress={onVerify}
        />
      </ScrollView>
    </SafeAreaView>
  );
}
